import { useScrollReveal, getRevealStyle } from '@/hooks/useScrollReveal';

const brandInfoCards = [
  {
    label: '正式名稱',
    value: '機曜文化事業有限公司',
    icon: 'ri-building-2-line',
  },
  {
    label: '網站簡稱',
    value: '機曜文化',
    icon: 'ri-booklet-line',
  },
  {
    label: '英文名稱',
    value: 'GIYAO',
    icon: 'ri-global-line',
  },
  {
    label: '出版定位',
    value: '科普類書籍',
    icon: 'ri-microscope-line',
  },
];

export default function BrandIntroSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-100 border-t border-primary-500/8"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        {/* Section label */}
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            About
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            02
          </span>
        </div>

        {/* Main heading */}
        <h2
          style={getRevealStyle(isVisible, 60)}
          className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-100 leading-[1.1] tracking-tight text-center text-glow"
        >
          認識機曜文化
        </h2>

        {/* Subtitle */}
        <p
          style={getRevealStyle(isVisible, 80)}
          className="mt-5 text-sm md:text-base text-primary-400 leading-[1.7] text-center font-label tracking-wide"
        >
          科普知識與主題閱讀
        </p>

        <div
          style={getRevealStyle(isVisible, 100)}
          className="mt-6 w-12 h-0.5 bg-primary-500 mx-auto shadow-[0_0_8px_var(--tw-shadow-color)] shadow-primary-500/40"
        />

        {/* Brand identity cards grid */}
        <div
          style={getRevealStyle(isVisible, 140)}
          className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-[960px] mx-auto"
        >
          {brandInfoCards.map((card) => (
            <div
              key={card.label}
              className="relative border border-primary-500/10 bg-background-50/40 px-5 py-5 group hover:border-primary-500/25 transition-all duration-300"
            >
              {/* Corner accent */}
              <span className="absolute top-0 left-0 w-3 h-px bg-primary-500/25 group-hover:bg-primary-500/50 transition-colors" />
              <span className="absolute top-0 left-0 w-px h-3 bg-primary-500/25 group-hover:bg-primary-500/50 transition-colors" />

              <div className="flex items-center gap-2.5 mb-3">
                <span className="w-6 h-6 flex items-center justify-center text-primary-400">
                  <i className={`${card.icon} text-sm`} />
                </span>
                <span className="text-[10px] font-medium text-foreground-600 tracking-widest uppercase font-label">
                  {card.label}
                </span>
              </div>
              <p className="text-sm md:text-base font-medium text-foreground-200 leading-[1.5]">
                {card.value}
              </p>
            </div>
          ))}
        </div>

        {/* Brand introduction text */}
        <div
          style={getRevealStyle(isVisible, 180)}
          className="mt-12 md:mt-16 max-w-[780px] mx-auto"
        >
          <p className="text-sm md:text-base text-foreground-300 leading-[1.9]">
            機曜文化以科普類書籍為主要出版方向，透過不同知識主題，提供適合一般讀者理解與閱讀的科普出版內容。網站呈現機曜文化的品牌定位、2026 最新發行書籍與相關出版資訊。
          </p>
        </div>

        {/* Publishing direction */}
        <div
          style={getRevealStyle(isVisible, 220)}
          className="mt-10 md:mt-12 max-w-[780px] mx-auto"
        >
          <div className="border border-primary-500/10 bg-background-50/40 px-6 py-6 md:px-8 md:py-7">
            <h3 className="text-xs text-primary-400 tracking-widest uppercase font-label mb-3">
              出版內容說明
            </h3>
            <p className="text-sm text-foreground-300 leading-[1.9]">
              以科學知識、自然現象與相關知識主題為主要出版方向，協助讀者透過書籍接觸不同領域的科普內容。
            </p>
          </div>
        </div>

        {/* Relationship statement */}
        <div
          style={getRevealStyle(isVisible, 260)}
          className="mt-6 max-w-[780px] mx-auto"
        >
          <div className="relative border border-primary-500/15 bg-background-50/40 px-6 py-6 md:px-8 md:py-7">
            {/* Corner accents */}
            <span className="absolute top-0 left-0 w-4 h-px bg-primary-500/40" />
            <span className="absolute top-0 left-0 w-px h-4 bg-primary-500/40" />
            <span className="absolute bottom-0 right-0 w-4 h-px bg-primary-500/40" />
            <span className="absolute bottom-0 right-0 w-px h-4 bg-primary-500/40" />

            <h3 className="text-xs text-primary-400 tracking-widest uppercase font-label mb-3">
              品牌與崧博出版的關係
            </h3>
            <p className="text-sm text-foreground-300 leading-[1.9]">
              本品牌委託崧博出版經銷，官方網站由崧博出版製作。
            </p>
            <p className="mt-3 text-xs text-foreground-600 leading-[1.8]">
              機曜文化是具有自身出版定位的出版品牌，委託經銷與網站製作不代表機曜文化為崧博旗下品牌、子品牌或自有品牌。
            </p>
          </div>
        </div>

        {/* Catalog query section */}
        <div
          style={getRevealStyle(isVisible, 300)}
          className="mt-10 md:mt-12 max-w-[780px] mx-auto"
        >
          <div className="border border-primary-500/10 bg-background-50/40 px-6 py-6 md:px-8 md:py-7">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xs text-primary-400 tracking-widest uppercase font-label mb-2">
                  書目查詢
                </h3>
                <p className="text-sm text-foreground-300 leading-[1.7] max-w-[480px]">
                  完整書名、作者與出版資訊，請前往崧博書籍資料庫查詢。
                </p>
              </div>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15 shrink-0"
              >
                前往書籍資料庫
                <i className="ri-arrow-right-up-line text-xs" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}